import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { isFleetOnly } from "../config/productConfig";
import SinoPessoal from "../components/student/SinoPessoal";
import { ChevronLeft, ChevronRight, CalendarCheck, ArrowLeft, FileText } from "lucide-react";

// Slots de 1 hora à hora certa, em UTC (como o servidor). Reserva com 2 horas de antecedência; cancelamento até 2 horas antes.
const HORAS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const UMA_HORA = 3600000;
const DUAS_HORAS = 2 * UMA_HORA;
const TIPOS = [
  { value: "voo", label: "Voo" },
  { value: "instrucao", label: "Instrução" },
  { value: "passageiro", label: "Passageiro" },
];

const todayUtc = () => new Date().toISOString().slice(0, 10);
const addDays = (day, n) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const pad = (h) => String(h).padStart(2, "0");
const erro = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};
// Papel de cada pessoa nas listas: instrutor, aluno (cartão de aluno) ou piloto
const papel = (p) => (p.kind === "instructor" ? "instrutor" : p.aluno ? "aluno" : "piloto");
const fmtQuando = (iso) => {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 10)} · ${pad(d.getUTCHours())}:00–${pad((d.getUTCHours() + 1) % 24)}:00 UTC`;
};

export default function ReservationsPage() {
  const navigate = useNavigate();
  const { user, school } = useAuth();
  // O administrador (que também é piloto ou instrutor) volta ao seu painel
  const eAdmin = ["admin", "super_admin"].includes(user?.role);
  const ehAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [emNomeDe, setEmNomeDe] = useState("");
  const [dia, setDia] = useState(todayUtc());
  const [opcoes, setOpcoes] = useState(null);
  const [semFicha, setSemFicha] = useState(false);
  const [ocupacao, setOcupacao] = useState({});
  const [minhas, setMinhas] = useState([]);
  const [tipo, setTipo] = useState("voo");
  const [escolha, setEscolha] = useState(null);
  const [parceiro, setParceiro] = useState("");
  const [aGravar, setAGravar] = useState(false);
  // Quem reserva é instrutor (a própria ficha, ou a pessoa escolhida pelo administrador): na instrução escolhe o aluno
  const quemReservaKind = emNomeDe ? (opcoes?.pessoas || []).find((p) => p.id === emNomeDe)?.kind : opcoes?.eu?.kind;
  const instrutorReserva = quemReservaKind === "instructor";

  const carregarOpcoes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/di/reservations/options`, { withCredentials: true });
      setOpcoes(data);
    } catch (e) {
      if (e?.response?.status === 403) setSemFicha(true);
      else toast.error(erro(e, "Não foi possível carregar as reservas."));
    }
  }, []);

  const carregarMinhas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/di/reservations/mine`, { withCredentials: true });
      setMinhas(data || []);
    } catch {
      setMinhas([]);
    }
  }, []);

  const carregarOcupacao = useCallback(async (aeronaves, dayStr) => {
    const pares = await Promise.all(
      aeronaves.filter((a) => !a.bloqueada).map(async (a) => {
        const { data } = await axios.get(`${API}/di/reservations/busy`, {
          params: { aircraft_registration: a.registration, date: dayStr },
          withCredentials: true,
        });
        return [a.registration, data || []];
      }),
    );
    setOcupacao(Object.fromEntries(pares));
  }, []);

  useEffect(() => { carregarOpcoes(); carregarMinhas(); }, [carregarOpcoes, carregarMinhas]);

  useEffect(() => {
    if (opcoes?.aircraft) carregarOcupacao(opcoes.aircraft, dia).catch(() => toast.error("Não foi possível ver a ocupação do dia."));
  }, [opcoes, dia, carregarOcupacao]);

  useEffect(() => { setEscolha(null); setParceiro(""); }, [dia, tipo]);
  useEffect(() => {
    if (!emNomeDe || !opcoes) return;
    const ainda = (opcoes.pessoas || []).find((p) => p.id === emNomeDe);
    const serve = ainda && (tipo === "instrucao" ? ainda.aluno && ainda.valido : tipo === "voo" ? ainda.valido : true);
    if (!serve) setEmNomeDe("");
  }, [tipo, emNomeDe, opcoes]);

  const estadoSlot = (reg, h) => {
    const inicio = Date.parse(`${dia}T${pad(h)}:00:00Z`);
    if (inicio < Date.now() + DUAS_HORAS) return "fechado";
    const ocupado = (ocupacao[reg] || []).some(
      (b) => Date.parse(b.starts_at) < inicio + UMA_HORA && Date.parse(b.ends_at) > inicio,
    );
    if (ocupado) return "ocupado";
    if (escolha && escolha.reg === reg && escolha.hora === h) return "escolhido";
    return "livre";
  };

  const reservar = async () => {
    if (!escolha) return;
    const corpo = {
      aircraft_registration: escolha.reg,
      starts_at: `${dia}T${pad(escolha.hora)}:00:00+00:00`,
      kind: tipo,
    };
    if (ehAdmin && emNomeDe) corpo.em_nome_de = emNomeDe;
    if (tipo === "instrucao") corpo[instrutorReserva ? "aluno_id" : "instructor_id"] = parceiro;
    if (tipo === "passageiro") corpo.pic_pilot_id = parceiro;
    if (ehAdmin && !emNomeDe && !opcoes?.eu) {
      toast.error("Escolhe em nome de quem fazes a reserva.");
      return;
    }
    if ((tipo === "instrucao" || tipo === "passageiro") && !parceiro) {
      toast.error(tipo === "instrucao" ? (instrutorReserva ? "Escolhe o aluno." : "Escolhe o instrutor.") : "Escolhe o piloto que vai aos comandos.");
      return;
    }
    setAGravar(true);
    try {
      await axios.post(`${API}/di/reservations`, corpo, { withCredentials: true });
      toast.success("Reserva feita.");
      setEscolha(null);
      await Promise.all([carregarMinhas(), opcoes && carregarOcupacao(opcoes.aircraft, dia)]);
    } catch (e) {
      toast.error(erro(e, "Não foi possível reservar."));
    } finally {
      setAGravar(false);
    }
  };

  const cancelar = async (id) => {
    try {
      await axios.post(`${API}/di/reservations/${id}/cancel`, {}, { withCredentials: true });
      toast.success("Reserva cancelada.");
      await Promise.all([carregarMinhas(), opcoes && carregarOcupacao(opcoes.aircraft, dia)]);
    } catch (e) {
      toast.error(erro(e, "Não foi possível cancelar."));
    }
  };

  if (semFicha) {
    return (
      <div className="min-h-screen bg-cockpit-bg p-4">
        {(eAdmin || !isFleetOnly(school)) && <Button variant="ghost" onClick={() => navigate(eAdmin ? "/admin" : "/dashboard")}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>}
        <p className="text-slate-300 mt-6 max-w-md">A tua conta não está ligada a uma ficha de piloto ou de instrutor desta escola. Pede ao administrador para a ligar.</p>
      </div>
    );
  }

  const aeronaves = opcoes?.aircraft || [];
  // Pedido de 22/10: só voa (aos comandos ou em instrução) quem tem os documentos válidos; como passageiro, qualquer um.
  // Instrução: «Em nome de» só alunos válidos. Passageiro: aos comandos só pilotos (não alunos) e instrutores válidos.
  const pessoasEmNomeDe = (opcoes?.pessoas || []).filter((p) =>
    tipo === "instrucao" ? p.aluno && p.valido : tipo === "voo" ? p.valido : true);
  const quemReserva = emNomeDe ? (opcoes?.pessoas || []).find((p) => p.id === emNomeDe) : opcoes?.eu;
  const parceiros = tipo === "instrucao"
    ? (instrutorReserva ? (opcoes?.alunos || []) : (opcoes?.instructors || [])).filter((p) => p.valido)
    : tipo === "passageiro"
      ? [...(opcoes?.pilots || []).filter((p) => !p.aluno), ...(opcoes?.instructors || [])]
        .filter((p) => p.valido && p.id !== quemReserva?.id)
      : [];
  // Quem reserva tem documentos inválidos: só pode ir como passageiro
  const bloqueadoPorDocumentos = tipo !== "passageiro" && quemReserva && quemReserva.valido === false;

  return (
    <div className="min-h-screen bg-cockpit-bg p-4 space-y-4" data-testid="reservas-piloto">
      <div className="flex items-center justify-between">
        {(eAdmin || !isFleetOnly(school)) && <Button variant="ghost" onClick={() => navigate(eAdmin ? "/admin" : "/dashboard")}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>}
        <div className="flex items-center gap-1">
          {!eAdmin && <SinoPessoal />}
          <Button variant="ghost" onClick={() => navigate("/meus-documentos")} data-testid="reservas-meus-documentos"><FileText className="w-4 h-4 mr-1" /> Os meus documentos</Button>
        </div>
      </div>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-orange-300"><CalendarCheck className="w-5 h-5" /> Reservar voo</CardTitle>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button variant="outline" size="icon" aria-label="Dia anterior" onClick={() => setDia(addDays(dia, -1))}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-slate-200 font-medium">{dia} <span className="text-[11px] text-slate-500">UTC</span></span>
            <Button variant="outline" size="icon" aria-label="Dia seguinte" onClick={() => setDia(addDays(dia, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2" role="radiogroup" aria-label="Tipo de reserva">
            {TIPOS.map((t) => (
              <Button key={t.value} size="sm" variant={tipo === t.value ? "default" : "outline"} onClick={() => setTipo(t.value)} data-testid={`reserva-tipo-${t.value}`}>{t.label}</Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          {aeronaves.length === 0 && <p className="text-slate-400">Não há aeronaves nesta escola.</p>}
          {aeronaves.map((a) => (
            <div key={a.registration} className="border border-slate-700 rounded-lg p-3" data-testid={`reserva-aeronave-${a.registration}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-slate-200 font-medium font-mono">{a.registration} <span className="text-slate-500 font-sans">{a.model || ""}</span></span>
                {a.bloqueada
                  ? <span className="text-red-300 text-[11px]">Bloqueada: {a.motivo}</span>
                  : <span className="text-emerald-300 text-[11px]">Disponível</span>}
              </div>
              {!a.bloqueada && (
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
                  {HORAS.map((h) => {
                    const estado = estadoSlot(a.registration, h);
                    const cls = {
                      livre: "border-slate-600 text-slate-300 hover:bg-slate-800",
                      ocupado: "bg-slate-800/60 text-slate-500 border-transparent cursor-not-allowed",
                      fechado: "bg-slate-800/40 text-slate-600 border-transparent cursor-not-allowed",
                      escolhido: "bg-orange-500/80 text-slate-950 border-transparent",
                    }[estado];
                    return (
                      <button
                        key={h}
                        type="button"
                        disabled={estado === "ocupado" || estado === "fechado"}
                        onClick={() => setEscolha({ reg: a.registration, hora: h })}
                        className={`h-8 rounded border text-[11px] ${cls}`}
                        aria-label={`${a.registration} às ${pad(h)}:00 UTC, ${estado === "livre" ? "livre" : estado === "ocupado" ? "ocupada" : estado === "fechado" ? "fechada" : "escolhida"}`}
                        data-testid={`reserva-slot-${a.registration}-${h}`}
                      >
                        {pad(h)}:00
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {ehAdmin && (
            <div className="space-y-1">
              <label className="text-slate-400 text-[12px]" htmlFor="reserva-em-nome-de">Em nome de</label>
              <select
                id="reserva-em-nome-de"
                value={emNomeDe}
                onChange={(e) => { setEmNomeDe(e.target.value); setParceiro(""); }}
                className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100"
                data-testid="reserva-em-nome-de"
              >
                <option value="">{opcoes?.eu ? `Eu (${opcoes.eu.name})` : "Escolher…"}</option>
                {pessoasEmNomeDe.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({papel(p)}, {p.aluno ? "cartão" : "PU"} {p.pu_number})</option>
                ))}
              </select>
            </div>
          )}
          {parceiros.length > 0 && (
            <div className="space-y-1">
              <label className="text-slate-400 text-[12px]" htmlFor="reserva-parceiro">{tipo === "instrucao" ? (instrutorReserva ? "Aluno (a reserva fica em nome dele; tu és o instrutor)" : "Instrutor (fica reservado também)") : "Piloto que vai aos comandos"}</label>
              <select
                id="reserva-parceiro"
                value={parceiro}
                onChange={(e) => setParceiro(e.target.value)}
                className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100"
                data-testid="reserva-parceiro"
              >
                <option value="">Escolher…</option>
                {parceiros.map((p) => <option key={p.id} value={p.id}>{p.name} ({papel(p)}, {p.aluno ? "cartão" : "PU"} {p.pu_number})</option>)}
              </select>
            </div>
          )}
          {(tipo === "instrucao" || tipo === "passageiro") && parceiros.length === 0 && opcoes && (
            <p className="text-amber-300 text-[12px]" data-testid="reserva-sem-parceiros">
              {tipo === "passageiro" ? "Não há pilotos nem instrutores com os documentos válidos para ir aos comandos."
                : instrutorReserva ? "Não há alunos com os documentos válidos." : "Não há instrutores com os documentos válidos."}
            </p>
          )}
          {bloqueadoPorDocumentos && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 text-[12px]" data-testid="reserva-documentos-invalidos">
              {emNomeDe ? `${quemReserva.name} tem documentos que não estão válidos, por isso só pode` : "Tens documentos que não estão válidos, por isso só podes"} reservar como <strong>passageiro</strong>:
              <ul className="list-disc pl-5 mt-1">{(quemReserva.motivos || []).map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-slate-300">
              {escolha ? `${escolha.reg} · ${pad(escolha.hora)}:00–${pad(escolha.hora + 1)}:00 UTC · ${TIPOS.find((t) => t.value === tipo)?.label.toLowerCase()}` : "Escolhe um horário livre."}
            </span>
            <Button onClick={reservar} disabled={!escolha || aGravar || bloqueadoPorDocumentos} data-testid="reserva-confirmar">Reservar</Button>
          </div>
          <p className="text-[11px] text-slate-500">Reserva com 2 horas de antecedência e até 30 dias à frente. Cancelas até 2 horas antes.</p>
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-200">As minhas reservas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs" data-testid="reserva-minhas">
          {minhas.length === 0 && <p className="text-slate-400">Ainda não tens reservas.</p>}
          {minhas.map((r) => {
            const podeCancelar = Date.parse(r.starts_at) - Date.now() >= DUAS_HORAS;
            return (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-2">
                <span className="text-slate-200">
                  {fmtQuando(r.starts_at)} · <span className="font-mono">{r.aircraft_registration}</span> · {TIPOS.find((t) => t.value === r.kind)?.label.toLowerCase() || r.kind}
                  {r.instructor_name ? ` com ${r.instructor_name}` : ""}
                </span>
                {r.status === "active"
                  ? (podeCancelar
                    ? <Button variant="outline" size="sm" onClick={() => cancelar(r.id)}>Cancelar</Button>
                    : <span className="text-slate-500 text-[11px]">Cancelamento fechado</span>)
                  : <span className="text-slate-500 text-[11px]">Cancelada</span>}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
